import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PenTool, Type, Upload, CheckCircle2, XCircle, Clock, Ban, FileText, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const INK = '#12284b';

const DrawPad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const inked = useRef(false);

  useEffect(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvasRef.current.width / r.width), y: (e.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); inked.current = true; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (inked.current) onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); inked.current = false; onChange(null); };

  return (
    <div>
      <canvas ref={canvasRef} width={700} height={200}
        className="w-full h-[170px] border-2 border-dashed border-slate-300 rounded-xl bg-white cursor-crosshair"
        style={{ touchAction: 'none' }}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        aria-label="Signature pad — draw your signature" data-testid="sign-draw-pad" />
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-500">Draw your signature with a mouse or finger</p>
        <button type="button" onClick={clear} className="text-xs font-semibold text-teal-700 underline" data-testid="sign-draw-clear">Clear</button>
      </div>
    </div>
  );
};

const typedToPng = (text) => {
  const canvas = document.createElement('canvas');
  canvas.width = 900; canvas.height = 240;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = INK;
  ctx.textBaseline = 'middle';
  let size = 110;
  ctx.font = `${size}px "Great Vibes", "Brush Script MT", cursive`;
  while (size > 28 && ctx.measureText(text).width > 860) { size -= 5; ctx.font = `${size}px "Great Vibes", "Brush Script MT", cursive`; }
  ctx.fillText(text, 20, 125);
  return canvas.toDataURL('image/png');
};

const StatusScreen = ({ icon: Icon, color, title, children }) => (
  <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm" data-testid="sign-status-screen">
    <Icon className={`w-14 h-14 mx-auto mb-4 ${color}`} />
    <h2 className="text-2xl font-bold text-slate-800 mb-2">{title}</h2>
    <div className="text-sm text-slate-500">{children}</div>
  </div>
);

export default function CertificateSigningPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState('draw');
  const [drawData, setDrawData] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [uploadData, setUploadData] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/cert/sign/${token}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'This signing link is not valid');
      setInfo(d);
      if (d.certificates) setSelected(d.certificates.map(c => c.id));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onUpload = (file) => {
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file (PNG or JPG)'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setUploadData(reader.result);
    reader.readAsDataURL(file);
  };

  const signatureData = () => {
    if (method === 'draw') return drawData;
    if (method === 'type') return typedName.trim() ? typedToPng(typedName.trim()) : null;
    return uploadData;
  };

  const submit = async () => {
    if (method === 'type' && typedName.trim()) {
      try { await document.fonts.load('110px "Great Vibes"'); } catch { /* fallback font */ }
    }
    const sig = signatureData();
    if (!sig) { toast.error(method === 'type' ? 'Type your signature first' : method === 'upload' ? 'Upload a signature image first' : 'Draw your signature first'); return; }
    if (selected.length === 0) { toast.error('Select at least one certificate to sign'); return; }
    if (!consent) { toast.error('Please agree to the electronic signature consent'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cert/sign/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ certificate_ids: selected, signature_data: sig, method, consent })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Signing failed');
      setResult(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!declineReason.trim()) { toast.error('Please provide a reason for declining'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cert/sign/${token}/decline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not decline');
      setDeclined(true);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const body = () => {
    if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-teal-600 animate-spin" /></div>;
    if (error) return <StatusScreen icon={XCircle} color="text-rose-500" title="Link Not Valid">{error}</StatusScreen>;
    if (result) return (
      <StatusScreen icon={CheckCircle2} color="text-emerald-500" title="Thank You — Signatures Applied">
        <p>Your signature was applied to <strong>{result.signed}</strong> certificate{result.signed !== 1 ? 's' : ''}.{result.skipped > 0 && ` ${result.skipped} certificate${result.skipped !== 1 ? 's were' : ' was'} left unsigned as you chose.`}</p>
        {result.failed?.length > 0 && <p className="text-rose-600 mt-2">Could not be signed: {result.failed.join(', ')} — the administrator has been notified in the logs.</p>}
        <p className="mt-3">The DC Early Learning Collaborative team will complete the final review before certificates are delivered. You may close this page.</p>
      </StatusScreen>
    );
    if (declined) return <StatusScreen icon={Ban} color="text-slate-400" title="Request Declined">You have declined this signature request. The administrator has been notified. You may close this page.</StatusScreen>;
    if (info.status === 'signed') return <StatusScreen icon={CheckCircle2} color="text-emerald-500" title="Already Completed">You have already signed {info.signed_count} certificate{info.signed_count !== 1 ? 's' : ''} through this link. No further action is needed.</StatusScreen>;
    if (info.status === 'declined') return <StatusScreen icon={Ban} color="text-slate-400" title="Request Declined">This signature request was declined{info.decline_reason ? ` ("${info.decline_reason}")` : ''}. Contact the administrator if this was a mistake.</StatusScreen>;
    if (info.status === 'cancelled') return <StatusScreen icon={Ban} color="text-slate-400" title="Request Cancelled">This signature request was cancelled by the administrator. No action is needed.</StatusScreen>;
    if (info.status === 'expired') return <StatusScreen icon={Clock} color="text-amber-500" title="Link Expired">This signing link has expired. Please ask the DCELC administrator to send you a fresh link.</StatusScreen>;

    const t = info.training || {};
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-teal-700 uppercase tracking-wider mb-2"><ShieldCheck className="w-4 h-4" /> Secure Signing Request</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{[t.date, t.hours, t.organization].filter(Boolean).join(' · ')}</p>
          <p className="text-sm text-slate-600 mt-3">
            Hello <strong>{info.signer?.name}</strong>{info.signer?.title ? ` (${info.signer.title})` : ''} — you've been asked to sign the certificates below.
            Review each one, untick any you don't wish to sign, then add your signature. It will be applied to <strong>all selected certificates at once</strong>.
          </p>
          {info.message && <p className="mt-3 text-sm text-slate-600 bg-amber-50 border-l-4 border-amber-400 px-4 py-2 rounded-r">{info.message}</p>}
          <p className="text-xs text-slate-400 mt-3">Link expires {new Date(info.expires_at).toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-slate-800">Certificates to Sign ({selected.length} of {info.certificates.length} selected)</h2>
            <button onClick={() => setSelected(selected.length === info.certificates.length ? [] : info.certificates.map(c => c.id))} className="text-xs font-semibold text-teal-700 underline" data-testid="sign-toggle-all">
              {selected.length === info.certificates.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto border border-slate-100 rounded-xl">
            {info.certificates.map(c => (
              <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`sign-cert-row-${c.id}`}>
                <input type="checkbox" className="h-4 w-4" checked={selected.includes(c.id)}
                  onChange={e => setSelected(v => e.target.checked ? [...v, c.id] : v.filter(x => x !== c.id))}
                  data-testid={`sign-cert-check-${c.id}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{c.participant_name}</p>
                  <p className="text-xs text-slate-400 font-mono">{c.cert_number}</p>
                </div>
                <a href={`${API}/api/cert/sign/${token}/preview/${c.id}`} target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline" data-testid={`sign-cert-preview-${c.id}`}>
                  <FileText className="w-3.5 h-3.5" /> Preview
                </a>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h2 className="text-base font-bold text-slate-800 mb-4">Your Signature</h2>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-5 w-fit" role="group" aria-label="Signature method">
            {[['draw', PenTool, 'Draw'], ['type', Type, 'Type'], ['upload', Upload, 'Upload']].map(([m, Icon, label]) => (
              <button key={m} onClick={() => setMethod(m)} data-testid={`sign-method-${m}`}
                className={`inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold ${method === m ? 'bg-[#0a1628] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {method === 'draw' && <DrawPad onChange={setDrawData} />}
          {method === 'type' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="typed-sig">Type your full name</label>
              <input id="typed-sig" value={typedName} onChange={e => setTypedName(e.target.value)} placeholder={info.signer?.name}
                className="w-full max-w-md border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" data-testid="sign-typed-input" />
              {typedName.trim() && (
                <div className="mt-3 border-2 border-dashed border-slate-200 rounded-xl px-6 py-4 bg-white w-fit">
                  <p style={{ fontFamily: '"Great Vibes", "Brush Script MT", cursive', fontSize: '42px', color: INK, lineHeight: 1.2 }} data-testid="sign-typed-preview">{typedName}</p>
                </div>
              )}
            </div>
          )}
          {method === 'upload' && (
            <div>
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:border-teal-400 cursor-pointer">
                <Upload className="w-4 h-4" /> Choose signature image
                <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="sign-upload-input"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
              </label>
              <p className="text-xs text-slate-400 mt-1.5">PNG or JPG, under 5 MB. A white background will be made transparent automatically.</p>
              {uploadData && <img src={uploadData} alt="Uploaded signature preview" className="mt-3 max-h-28 border-2 border-dashed border-slate-200 rounded-xl p-2 bg-white" data-testid="sign-upload-preview" />}
            </div>
          )}

          <label className="flex items-start gap-3 mt-6 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="h-4 w-4 mt-0.5" data-testid="sign-consent-checkbox" />
            <span className="text-xs text-slate-600 leading-relaxed">{info.consent_text}</span>
          </label>

          <div className="flex items-center gap-4 mt-6 flex-wrap">
            <button onClick={submit} disabled={busy} data-testid="sign-submit-btn"
              className="inline-flex items-center gap-2 px-7 py-3 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-full disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />}
              Sign {selected.length} Certificate{selected.length !== 1 ? 's' : ''}
            </button>
            <button onClick={() => setDeclining(v => !v)} className="text-sm font-semibold text-rose-600 hover:underline" data-testid="sign-decline-toggle">Decline to sign</button>
          </div>

          {declining && (
            <div className="mt-4 border border-rose-200 bg-rose-50/50 rounded-xl p-4" data-testid="sign-decline-form">
              <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="decline-reason">Reason for declining (required)</label>
              <textarea id="decline-reason" rows={2} value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="sign-decline-reason" />
              <button onClick={decline} disabled={busy} className="mt-2 px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50" data-testid="sign-decline-submit">Confirm Decline</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-[#0a1628] py-5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-[#d4a017] text-xs font-bold tracking-[0.2em] uppercase">DC Early Learning Collaborative</p>
          <p className="text-white text-lg font-bold">Certificate Signing Portal</p>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className="px-4 sm:px-6 py-10">
        {body()}
      </main>
      <footer className="pb-8 text-center text-xs text-slate-400">Secure electronic signing · DC Early Learning Collaborative</footer>
    </div>
  );
}
